import { useCallback, useEffect, useRef } from 'react';
import Axios, { CancelTokenSource } from 'axios';
import type { Editor } from '@tiptap/react';

import API from 'utils/api';
import { getErrorMessage } from 'utils/api/errors';
import { useStore } from 'utils/hooks/store';
import { uploadHeaderFooterImage } from 'containers/Tiptap/lib/header-footer-image-upload';
import { openHeaderFooterKey, readOpenHeaderFooter } from 'containers/Tiptap/lib/header-footer-zone';

export const NO_COMPANY_LOGO_MESSAGE =
  'Your organisation has no company logo yet. An Admin can add one in Company settings.';
const IMAGE_FAILED_MESSAGE = 'Failed to upload image';
const LOGO_FAILED_MESSAGE = 'Failed to insert company logo';

interface HeaderFooterInserts {
  insertToken: (token: string) => void;
  insertImageFile: (file: File) => Promise<void>;
  insertCompanyLogo: () => Promise<void>;
}

/**
 * Inserts into whichever header or footer editor Pages has open.
 */
export const useHeaderFooterInserts = (bodyEditor: Editor, entityId: string | undefined): HeaderFooterInserts => {
  const { notification } = useStore();
  const cancelSourceRef = useRef<CancelTokenSource | null>(null);

  useEffect(
    () => () => {
      cancelSourceRef.current?.cancel();
      cancelSourceRef.current = null;
    },
    []
  );

  const getCancelSource = useCallback((): CancelTokenSource => {
    cancelSourceRef.current ??= Axios.CancelToken.source();
    return cancelSourceRef.current;
  }, []);

  const reportError = useCallback(
    (error: unknown, fallback: string): void => {
      if (Axios.isCancel(error)) return;
      notification.enqueueErrorSnackbar(Axios.isAxiosError(error) ? getErrorMessage(error, fallback) : fallback);
    },
    [notification]
  );

  const insertToken = useCallback(
    (token: string): void => {
      readOpenHeaderFooter(bodyEditor)?.editor.chain().focus().insertContent(token).run();
    },
    [bodyEditor]
  );

  const uploadToOpenZone = useCallback(
    async (file: File): Promise<void> => {
      const open = readOpenHeaderFooter(bodyEditor);
      if (!open) return;
      if (!entityId) throw new Error('No document to upload to');
      const openKey = openHeaderFooterKey(open);
      const isStillOpen = (): boolean => openHeaderFooterKey(readOpenHeaderFooter(bodyEditor)) === openKey;
      await uploadHeaderFooterImage(open.editor, entityId, file, getCancelSource(), isStillOpen);
    },
    [bodyEditor, entityId, getCancelSource]
  );

  const insertImageFile = useCallback(
    async (file: File): Promise<void> => {
      try {
        await uploadToOpenZone(file);
      } catch (error) {
        reportError(error, IMAGE_FAILED_MESSAGE);
      }
    },
    [reportError, uploadToOpenZone]
  );

  const insertCompanyLogo = useCallback(async (): Promise<void> => {
    try {
      const response = await API.organisations.loadOrganisationLogo(getCancelSource())();
      const blob = response.data;
      if (response.status === 204 || !blob || blob.size === 0) {
        notification.enqueueErrorSnackbar(NO_COMPANY_LOGO_MESSAGE);
        return;
      }
      await uploadToOpenZone(new File([blob], 'company-logo', { type: blob.type }));
    } catch (error) {
      reportError(error, LOGO_FAILED_MESSAGE);
    }
  }, [getCancelSource, notification, reportError, uploadToOpenZone]);

  return { insertToken, insertImageFile, insertCompanyLogo };
};
